<template>
  <div class="report-download-options">
    <oxd-button
      size="small"
      icon-name="file-spreadsheet-fill"
      label="Export CSV"
      display-type="text"
      :loading="isLoading"
      @click="downloadAsCSV"
    />
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import {computed, ref} from 'vue';
import {CellAdapter, OxdMultilineCell} from '@ohrm/oxd';

export default {
  name: 'ReportCSVDownload',

  props: {
    name: {
      type: String,
      required: true,
    },
    module: {
      type: String,
      required: true,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    prefetch: {
      type: Boolean,
      default: false,
    },
    details: {
      type: Object,
      default: () => ({}),
    },
    columnCount: {
      type: Number,
      default: null,
      required: false,
    },
  },

  setup(props) {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/${props.module}/reports/data`,
    );

    const headers = ref([]);
    const colCount = ref(props.columnCount || 0);

    const serializedFilters = computed(() => ({
      ...props.filters,
      name: props.name,
      _dateFormattingEnabled: true,
    }));

    const {
      total,
      pages,
      response,
      isLoading,
      currentPage,
      showPaginator,
      execQuery: fetchTableData,
    } = usePaginate(http, {
      query: serializedFilters,
      prefetch: false,
      pageSize: 0, // fetch all data
    });

    return {
      total,
      pages,
      headers,
      colCount,
      response,
      isLoading,
      currentPage,
      showPaginator,
      serializedFilters,
      fetchTableData,
      http,
    };
  },

  methods: {
    async downloadAsCSV() {
      try {
        this.isLoading = true;

        // 1️⃣ Fetch report headers
        const headerResponse = await this.http.request({
          method: 'GET',
          url: `/api/v2/${this.module}/reports`,
          params: {
            name: this.serializedFilters.name,
            reportId: this.serializedFilters?.reportId,
          },
        });

        const {data, meta} = headerResponse.data;
        const tableHeaders = data.headers.map((header) => {
          if (header?.children && Array.isArray(header.children)) {
            header.children = header.children.map((child) => ({
              ...child,
              cellTemplate:
                child.cellProperties?.type === 'list'
                  ? CellAdapter(OxdMultilineCell)
                  : undefined,
            }));
          }
          return header;
        });

        if (meta.headers?.columnCount) {
          this.colCount = meta.headers.columnCount;
        }

        // 2️⃣ Fetch table data
        await this.fetchTableData();
        const records = Array.isArray(this.response.data)
          ? this.response.data
          : [];

        // 3️⃣ Prepare CSV
        const filename = `Report_${new Date()
          .toISOString()
          .replace(/[^0-9]/g, '')
          .slice(0, 14)}.csv`;

        let csvContent = '';

        // -- Header Info (Details & Filters)
        const infoLines = [
          ['Name', this.details?.employeeName],
          ['Project Name', this.details?.projectName],
          ['Activity Name', this.details?.activityName],
          ['From Date', this.serializedFilters?.fromDate],
          ['To Date', this.serializedFilters?.toDate],
        ]
          .filter(([_, value]) => value)
          .map(([label, value]) => `"${label}:","${value}"`)
          .join('\n');

        if (infoLines) csvContent += infoLines + '\n\n';

        // -- CSV Headers
        const superHeaders = [];
        const subHeaders = [];

        tableHeaders.forEach((header) => {
          if (header.children && header.children.length) {
            superHeaders.push(`"${header.name.toUpperCase()}"`);
            for (let i = 1; i < header.children.length; i++) {
              superHeaders.push('');
            }
            subHeaders.push(
              ...header.children.map(
                (child) => `"${child.name.toUpperCase()}"`,
              ),
            );
          } else {
            superHeaders.push(`"${header.name.toUpperCase()}"`);
            subHeaders.push('');
          }
        });

        csvContent += superHeaders.join(',') + '\n';
        if (subHeaders.some((h) => h)) {
          csvContent += subHeaders.join(',') + '\n';
        }

        // -- CSV Rows
        records.forEach((item) => {
          const row = tableHeaders.flatMap((header) => {
            if (header.children && header.children.length) {
              return header.children.map((child) => {
                const val = item?.[child.prop] ?? '';
                return `"${String(val).replace(/"/g, '""')}"`;
              });
            } else {
              const val = item?.[header.prop] ?? '';
              return `"${String(val).replace(/"/g, '""')}"`;
            }
          });
          csvContent += row.join(',') + '\n';
        });

        // 4️⃣ Trigger download
        const blob = new Blob([csvContent], {
          type: 'text/csv;charset=utf-8;',
        });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.isLoading = false;
      } catch (error) {
        console.log('Error while generating CSV:', error);
        this.isLoading = false;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.report-download-options {
  ::v-deep(.oxd-button--text) {
    box-shadow: inset 0 1px 3px rgb(0 0 0 / 59%);
    min-width: 103px;
  }
}
</style>
